import React, { useState, useEffect, useMemo } from 'react';
import './SettingsDrawer.css';
import KontoSection from './sections/KontoSection';
import AllgemeinSection from './sections/AllgemeinSection';
import BrancheSection from './sections/BrancheSection';
import ModuleSection from './sections/ModuleSection';
import AgentsSection from './sections/AgentsSection';
import SchnellanfragenSection from './sections/SchnellanfragenSection';
import BenutzerSection from './sections/BenutzerSection';
import AuditSection from './sections/AuditSection';
import CustomFieldsSection from './sections/CustomFieldsSection';
import MandantenSection from './sections/MandantenSection';
import MitarbeiterSection from './sections/MitarbeiterSection';
import MetallSection from './sections/MetallSection';
import SteuersatzSection from './sections/SteuersatzSection';
import FirmaSection from './sections/FirmaSection';
import RechteSection from './sections/RechteSection';
import VersionFooter from './VersionFooter';
import McpSection from './sections/McpSection';
import { AuthUser } from '../../types/auth';
import { useAktiveModule } from '../../hooks/useAktiveModule';

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  authToken: string;
  tenantId: number | null;
  userRolle: string;
  authUser?: AuthUser | null;
  onOpenSuperAdmin?: () => void;
  /** Sektion, die beim Öffnen gezeigt wird (Board-Ziel „einstellungen“); unbekannte oder
   *  für den Benutzer unsichtbare Sektionen werden ignoriert. */
  initialSection?: string | null;
}

type SectionId =
  | 'allgemein' | 'konto' | 'branche' | 'module'
  | 'benutzer' | 'rechte' | 'audit' | 'custom_fields' | 'mitarbeiter' | 'metall' | 'steuersaetze' | 'firma'
  | 'agents' | 'schnellanfragen'
  | 'mcp' | 'mandanten';

type NavGruppe = 'Arbeitsbereich' | 'Verwaltung' | 'Automatisierung' | 'System';

// Reihenfolge, in der die Gruppen-Zwischenüberschriften angezeigt werden
const GRUPPEN_REIHENFOLGE: NavGruppe[] = ['Arbeitsbereich', 'Verwaltung', 'Automatisierung', 'System'];

interface NavItem {
  id: SectionId;
  label: string;
  gruppe: NavGruppe;
  /** Suchbegriffe, unter denen dieser Eintrag zusätzlich zum Label gefunden wird */
  keywords: string[];
  rollen?: string[];
  /** Falls gesetzt: Eintrag nur sichtbar wenn dieses Modul aktiv ist */
  modul?: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'konto',           label: 'Konto',           gruppe: 'Arbeitsbereich',   keywords: ['passwort', '2fa', 'zwei-faktor', 'sicherheit', 'geräte', 'authenticator'] },
  { id: 'allgemein',       label: 'Allgemein',       gruppe: 'Arbeitsbereich',   keywords: ['smtp', 'e-mail', 'ssl', 'konfiguration'] },
  { id: 'branche',         label: 'Branche',         gruppe: 'Arbeitsbereich',   keywords: ['branche', 'vorlage', 'template'] },
  { id: 'module',          label: 'Module',          gruppe: 'Arbeitsbereich',   keywords: ['module', 'funktionen', 'aktivieren'] },
  { id: 'schnellanfragen', label: 'Schnellanfragen',  gruppe: 'Arbeitsbereich',   keywords: ['chips', 'vorlagen', 'fragen'] },
  { id: 'benutzer',        label: 'Benutzer',         gruppe: 'Verwaltung',       keywords: ['rollen', 'passwort', 'zugriff'], rollen: ['Admin'] },
  { id: 'rechte',           label: 'Rechte',           gruppe: 'Verwaltung',       keywords: ['rollen', 'berechtigung', 'zuweisung', 'rechte'], rollen: ['Admin'] },
  { id: 'custom_fields',   label: 'Custom-Felder',    gruppe: 'Verwaltung',       keywords: ['felder', 'attribute'], rollen: ['Admin'] },
  { id: 'audit',           label: 'Audit',            gruppe: 'Verwaltung',       keywords: ['protokoll', 'log', 'nachvollziehbarkeit'], rollen: ['Admin', 'Geschäftsführer'] },
  { id: 'mitarbeiter',     label: 'Mitarbeiter',      gruppe: 'Verwaltung',       keywords: ['personal', 'team', 'stundensatz', 'qualifikation'], rollen: ['Admin', 'Geschäftsführer', 'Projektleiter', 'Personalverwaltung'], modul: 'mitarbeiter' },
  // Metallnotierungen haengen am Artikel-Modul: ohne Artikelstamm gibt es
  // keine Metallartikel, fuer die ein Tageskurs zu pflegen waere.
  { id: 'metall',          label: 'Metallnotierungen', gruppe: 'Verwaltung',      keywords: ['kupfer', 'kupferzuschlag', 'metallzuschlag', 'del-notiz', 'notierung', 'kabel', 'aluminium'], rollen: ['Admin', 'Geschäftsführer', 'Einkäufer', 'Verkäufer'], modul: 'artikel' },
  // Steuersaetze haengen wie die Metallnotierungen am Artikelstamm.
  { id: 'steuersaetze',    label: 'Steuersätze',       gruppe: 'Verwaltung',      keywords: ['steuer', 'umsatzsteuer', 'mwst', 'mehrwertsteuer', 'steuersatz', 'prozent'], rollen: ['Admin', 'Geschäftsführer'], modul: 'artikel' },
  { id: 'firma',           label: 'Firma',            gruppe: 'Verwaltung',
    keywords: ['briefkopf', 'logo', 'bank', 'iban', 'hrb', 'steuernummer', 'briefpapier', 'vorlage', 'firmendaten'],
    rollen: ['Admin', 'Geschäftsführer'] },
  { id: 'agents',          label: 'Agenten',          gruppe: 'Automatisierung',  keywords: ['ki', 'assistent', 'prompt'], rollen: ['Admin'] },
  { id: 'mcp',             label: 'MCP-Zugriff',      gruppe: 'Automatisierung',  keywords: ['mcp', 'token', 'claude', 'api', 'extern'], rollen: ['Admin'] },
  { id: 'mandanten',       label: 'Mandanten',        gruppe: 'System',           keywords: ['kunden', 'tenant', 'instanzen'], rollen: ['SuperAdmin'] },
];

const SettingsDrawer: React.FC<SettingsDrawerProps> = ({
  open, onClose, authToken, tenantId, userRolle, authUser, onOpenSuperAdmin, initialSection,
}) => {
  // Mandantenübergreifender Verwalter: SuperAdmin ODER firmenloser Admin (tenant_id IS NULL).
  // Ein tenant-gebundener (Kunden-)Admin darf die Mandantenverwaltung NICHT sehen.
  const istFleetAdmin = userRolle === 'SuperAdmin' || (userRolle === 'Admin' && tenantId == null);
  const aktiveModule = useAktiveModule();
  const sichtbareItems = NAV_ITEMS.filter(item => {
    // Solange das Modul-Set noch leer ist (Ladevorgang), nichts ausblenden — kein Flicker.
    const modulOk = !item.modul || aktiveModule.size === 0 || aktiveModule.has(item.modul);
    if (!modulOk) return false;
    if (!item.rollen) return true;
    if (item.id === 'mandanten') return istFleetAdmin;
    return item.rollen.includes(userRolle);
  });
  const [activeSection, setActiveSection] = useState<SectionId>('allgemein');
  const [suche, setSuche] = useState('');

  // Beim Öffnen (oder neuer Zielsektion bei offenem Drawer) auf die gewünschte Sektion
  // springen, sofern sie sichtbar ist; sonst bleibt die bisherige Sektion.
  useEffect(() => {
    if (!open || !initialSection) return;
    const ziel = sichtbareItems.find(i => i.id === initialSection);
    if (ziel) setActiveSection(ziel.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSection]);

  useEffect(() => {
    if (!sichtbareItems.find(i => i.id === activeSection)) {
      setActiveSection('allgemein');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRolle, aktiveModule]);

  const gefilterteItems = useMemo(() => {
    const query = suche.trim().toLowerCase();
    if (!query) return sichtbareItems;
    return sichtbareItems.filter(item =>
      item.label.toLowerCase().includes(query) ||
      item.keywords.some(k => k.toLowerCase().includes(query))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suche, userRolle, aktiveModule]);

  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  if (!open) return null;

  const renderSection = () => {
    switch (activeSection) {
      case 'konto':           return <KontoSection authToken={authToken} />;
      case 'allgemein':       return <AllgemeinSection authToken={authToken} />;
      case 'branche':         return <BrancheSection authToken={authToken} />;
      case 'module':          return <ModuleSection tenantId={tenantId} />;
      case 'benutzer':        return <BenutzerSection authToken={authToken} />;
      case 'rechte':          return <RechteSection />;
      case 'audit':           return <AuditSection authToken={authToken} />;
      case 'custom_fields':   return <CustomFieldsSection authToken={authToken} />;
      case 'mitarbeiter':     return <MitarbeiterSection />;
      case 'metall':          return <MetallSection />;
      case 'steuersaetze':    return <SteuersatzSection />;
      case 'firma':           return <FirmaSection />;
      case 'agents':          return <AgentsSection authToken={authToken} />;
      case 'mcp':             return <McpSection authToken={authToken} />;
      case 'schnellanfragen': return <SchnellanfragenSection authToken={authToken} />;
      case 'mandanten':
        return authUser ? <MandantenSection authUser={authUser} onOpenSuperAdmin={onOpenSuperAdmin} /> : null;
      default:
        return null;
    }
  };

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-drawer" onClick={e => e.stopPropagation()}>
        <div className="settings-drawer-header">
          Einstellungen
          <button className="settings-drawer-close" onClick={onClose}>✕</button>
        </div>
        <div className="settings-drawer-body">
          <nav className="settings-nav">
            <input
              type="text"
              className="settings-nav-search"
              placeholder="Einstellungen durchsuchen…"
              value={suche}
              onChange={e => setSuche(e.target.value)}
            />
            {gefilterteItems.length === 0 ? (
              <div className="settings-nav-empty">Keine Einstellungen gefunden</div>
            ) : (
              GRUPPEN_REIHENFOLGE.map(gruppe => {
                const items = gefilterteItems.filter(item => item.gruppe === gruppe);
                if (items.length === 0) return null;
                return (
                  <div key={gruppe} className="settings-nav-gruppe">
                    <div className="settings-nav-gruppe-titel">{gruppe}</div>
                    {items.map(item => (
                      <button
                        key={item.id}
                        className={`settings-nav-item ${activeSection === item.id ? 'active' : ''}`}
                        onClick={() => setActiveSection(item.id)}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                );
              })
            )}
            <VersionFooter />
          </nav>
          <div className="settings-content">
            {renderSection()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsDrawer;
